<script lang="ts">
  import "../app.scss";
  import Header from "$lib/views/Header.svelte";
  import Modal from "$lib/molecules/Modal.svelte";
  import {
    blockScroll,
    menuIsOpen,
    modalContent,
    modalIsOpened,
    pageMainColor,
    pageMainColorUsage,
    headerVariant,
    heroBackgroundColor,
    transitionState,
  } from "$stores";
  import { onMount, setContext } from "svelte";
  import { page } from "$app/stores";
  import PageTransition from "$lib/components/PageTransition.svelte";
  import { beforeNavigate, onNavigate, afterNavigate } from "$app/navigation";
  import { pageTransitionOut } from "$stores";
  import { lazyloadImg } from "$stores";
  import LazyLoad from "vanilla-lazyload";
  import { getClient } from "$lib/utils/client";
  import ToastBar from "$lib/molecules/ToastBar.svelte";
  import { setAppContext } from "$lib/utils/contexts";
  import {
    initBreakpointStore,
    initDisplayTypeStore,
  } from "$lib/utils/breakpoints.js";

  export let data;

  let headerOffsetTop = 0;

  getClient();

  setAppContext("footerNavigation", data.globals.story.content.footerNav!);
  setAppContext("socials", data.globals.story.content.socials!);
  setAppContext(
    "footerBottomLinks",
    data.globals.story.content.footerBottomLinks!,
  );

  // TODO: there is no "datasources" property on GlobalStoryblok
  // eslint-disable-next-line
  setAppContext("datasources", (data.globals as any).datasources);

  onNavigate(() => {
    if ($menuIsOpen) {
      $menuIsOpen = false;
    }

    if ($modalIsOpened) {
      $modalIsOpened = false;
    }
  });

  afterNavigate(() => {
    $pageTransitionOut = false;
  });

  beforeNavigate(() => {
    $pageTransitionOut = true;
  });

  onMount(() => {
    const cleanupDisplayStore = initDisplayTypeStore();
    const cleanupBreakpointStore = initBreakpointStore();

    page.subscribe(async (value) => {
      if (value.status === 200) {
        if (!$lazyloadImg) {
          $lazyloadImg = new LazyLoad({});
        } else {
          $lazyloadImg.update();
        }
      }
    });

    blockScroll.subscribe(async (value) => {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      const body = document.body;
      if (value === true) {
        body.style.height = `${window.innerHeight}px`;
        body.style.overflowY = "hidden";
        body.style.marginRight = `${scrollbarWidth}px`;
        body.style.width = `calc(100vw - ${scrollbarWidth}px)`;
      } else {
        body.style.overflowY = "scroll";
        body.style.height = "auto";
        body.style.marginRight = "0";
        body.style.width = "100%";
      }
    });

    $transitionState = "initial";

    return () => {
      cleanupDisplayStore();
      cleanupBreakpointStore();
    };
  });

  function onToastBarResize({ detail }: CustomEvent) {
    headerOffsetTop = detail.height;
  }

  // Used to set a canonical link header on the page. Most pages will refer to
  // themselves, but job posting pages will refer to the base posting page.
  $: canonicalUrl = $page.url.pathname.startsWith("/careers/open-roles")
    ? `${new URL("/careers/open-roles", $page.url)}`
    : `${$page.url}`;

  // Check if this is a Consumer or Business page
  $: isBusinessPage = $page.url.pathname.startsWith("/business");
  $: isConsumerPage = !isBusinessPage;
</script>

<svelte:head>
  <meta property="og:url" content={canonicalUrl} />
  <link rel="canonical" href={canonicalUrl} />
  {#if $page.url.hostname === "staging.flyzipline.com"}
    <script
      src="https://www.bugherd.com/sidebarv2.js?apikey=s3bjxesmnl0psh5gtjclya"
      async
      defer
    ></script>
  {/if}
</svelte:head>

<main
  style={`margin-top: ${headerOffsetTop}px`}
  class="main page-main-color-{$pageMainColor ||
    'none'} page-main-color-usage-{$pageMainColorUsage} header-variant-{$headerVariant}"
  data-sveltekit-noscroll
>
  <ToastBar
    blok={data.globals.story.content.toastBar}
    on:toastBarResize={onToastBarResize}
  />

  {#if isConsumerPage}
    <Header
      offsetTop={headerOffsetTop}
      header={data.globals.story.content.consumerHeaderNav}
      background={$heroBackgroundColor}
    />
  {/if}

  {#if isBusinessPage}
    <Header
      offsetTop={headerOffsetTop}
      header={data.globals.story.content.businessHeaderNav}
      background={$heroBackgroundColor}
    />
  {/if}

  <!-- {@html JSON.stringify()} -->
  <PageTransition pathname={data.pathname} on:transition>
    <slot />
  </PageTransition>
  {#if $modalIsOpened && $modalContent}
    <Modal blok={$modalContent} />
  {/if}
</main>

<style lang="scss">
  .main {
    --main-top-padding: 68px;
    padding: var(--main-top-padding) 0 0;

    @include breakpoint("small") {
      padding: var(--main-top-padding) 0 20px;
    }

    &.page-main-color-usage-hero-footer-full-bleed,
    &.page-main-color-usage-page {
      &.page-main-color-gray {
        background: rgb(var(--color-light-grey));
      }
      &.page-main-color-yellow {
        background: rgb(var(--color-yellow-primary));
      }
      &.page-main-color-green {
        background: rgb(var(--color-green-primary));
      }
      &.page-main-color-blue {
        background: rgb(var(--color-blue-primary));
      }
    }
  }

  /* Remove bottom padding for pages with no background color */
  @include breakpoint("small") {
    .main.page-main-color-none {
      padding-bottom: 0;
    }
  }
</style>
